import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_VARIANT_BY_MODE,
  isMobileThemeVariantId,
  MOBILE_THEME_VARIANT_META,
  normalizeMobileThemeVariantId,
} from '../theme/mobileTheme';

const MODE_STORAGE_KEY = 'mobileThemeMode';
const VARIANTS_STORAGE_KEY = 'mobileThemeVariants';

function getSystemMode() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredMode() {
  const stored = localStorage.getItem(MODE_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return null;
}

function readStoredVariants() {
  try {
    const raw = localStorage.getItem(VARIANTS_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_VARIANT_BY_MODE };
    }
    const parsed = JSON.parse(raw);
    return {
      light: normalizeMobileThemeVariantId(parsed.light, 'light'),
      dark: normalizeMobileThemeVariantId(parsed.dark, 'dark'),
    };
  } catch {
    return { ...DEFAULT_VARIANT_BY_MODE };
  }
}

function readThemeFromUrl() {
  if (typeof window === 'undefined') return null;
  const themeParam = new URLSearchParams(window.location.search).get('theme');
  if (!themeParam) return null;
  const id = themeParam.toUpperCase();
  return isMobileThemeVariantId(id) ? id : null;
}

function persistVariants(variants) {
  localStorage.setItem(VARIANTS_STORAGE_KEY, JSON.stringify(variants));
}

export function useMobileTheme() {
  const [storedMode, setStoredMode] = useState(() => readStoredMode());
  const [systemMode, setSystemMode] = useState(getSystemMode);
  const [variants, setVariants] = useState(() => readStoredVariants());

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const urlVariant = readThemeFromUrl();
    if (!urlVariant) return;

    const meta = MOBILE_THEME_VARIANT_META[urlVariant];
    setStoredMode(meta.mode);
    localStorage.setItem(MODE_STORAGE_KEY, meta.mode);
    setVariants((prev) => {
      const next = {
        ...prev,
        [meta.mode]: urlVariant,
      };
      persistVariants(next);
      return next;
    });
  }, []);

  const mode = storedMode ?? systemMode;

  const variant = useMemo(
    () => normalizeMobileThemeVariantId(variants[mode], mode),
    [mode, variants]
  );

  const toggleMode = useCallback(() => {
    setStoredMode((prev) => {
      const current = prev ?? systemMode;
      const next = current === 'dark' ? 'light' : 'dark';
      localStorage.setItem(MODE_STORAGE_KEY, next);
      return next;
    });
  }, [systemMode]);

  const setVariant = useCallback((variantId) => {
    const id = typeof variantId === 'string' ? variantId.toUpperCase() : '';
    if (!isMobileThemeVariantId(id)) return;

    const meta = MOBILE_THEME_VARIANT_META[id];
    setStoredMode(meta.mode);
    localStorage.setItem(MODE_STORAGE_KEY, meta.mode);
    setVariants((prev) => {
      const next = {
        ...prev,
        [meta.mode]: id,
      };
      persistVariants(next);
      return next;
    });
  }, []);

  const isSystemDefault = storedMode === null;

  return useMemo(
    () => ({ mode, variant, variants, toggleMode, setVariant, isSystemDefault }),
    [mode, variant, variants, toggleMode, setVariant, isSystemDefault]
  );
}
